import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Download, BookOpen, LogIn, ArrowUpRight } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";

export default function Percorso() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState("loading");
  const [data, setData] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user) { setState("login"); return; }
    api.get("/program")
      .then((r) => { setData(r.data); setState("ok"); })
      .catch((e) => setState(e.response?.status === 403 ? "notowned" : "login"));
  }, [user, loading]);

  if (state === "loading" || loading) {
    return <div className="max-w-4xl mx-auto px-4 py-32"><div className="h-64 bg-[#F3E8E2] animate-pulse rounded-2xl" /></div>;
  }
  if (state === "login") {
    return (
      <div className="min-h-[50vh] flex flex-col items-center justify-center text-center px-6">
        <h1 className="font-serif text-3xl mb-4">Accedi per continuare</h1>
        <p className="text-[#8C7E83] mb-8">Entra per aprire il tuo percorso.</p>
        <button onClick={login} className="inline-flex items-center gap-2 bg-[#C6899E] text-[#FBF5F1] rounded-full px-7 py-3.5 text-sm uppercase tracking-[0.15em] hover:bg-[#B4718C]">
          <LogIn className="w-4 h-4" /> Accedi
        </button>
      </div>
    );
  }
  if (state === "notowned") {
    return (
      <div className="min-h-[50vh] flex flex-col items-center justify-center text-center px-6">
        <h1 className="font-serif text-3xl mb-4">Non ancora tuo</h1>
        <p className="text-[#8C7E83] mb-8">Acquista il prodotto per accedere al percorso.</p>
        <Link to="/" className="inline-flex bg-[#C6899E] text-[#FBF5F1] rounded-full px-7 py-3.5 text-sm uppercase tracking-[0.15em] hover:bg-[#B4718C]">Scopri il prodotto</Link>
      </div>
    );
  }

  const paras = (t) => (t || "").split("\n\n").filter(Boolean);

  return (
    <div data-testid="percorso-page">
      <PageHeader eyebrow="Il mio percorso" title={data.home?.title || "eBook + 21 giorni"} />

      <div className="max-w-3xl mx-auto px-4 sm:px-8 pb-8">
        {paras(data.intro?.text).length > 0 && (
          <div className="prose-unfiltered mb-14">{paras(data.intro.text).map((p, i) => <p key={i}>{p}</p>)}</div>
        )}

        {/* eBook */}
        <section className="rounded-3xl bg-[#FFFFFF] border border-[rgba(61,50,56,0.1)] p-6 sm:p-8 mb-10" data-testid="percorso-ebook">
          <div className="eyebrow mb-4 flex items-center gap-2"><BookOpen className="w-4 h-4" /> eBook</div>
          <div className="flex flex-col sm:flex-row gap-6">
            <div className="w-40 aspect-[3/4] rounded-xl overflow-hidden bg-[#F3E8E2] shrink-0 mx-auto sm:mx-0">
              {data.ebook?.cover_path ? <img src={mediaUrl(data.ebook.cover_path)} alt="eBook" className="w-full h-full object-cover" /> : null}
            </div>
            <div className="flex-1">
              {data.ebook?.description && <p className="text-[#8C7E83] leading-relaxed mb-5">{data.ebook.description}</p>}
              {data.ebook?.pdf_path && (
                <a href={mediaUrl(data.ebook.pdf_path)} download target="_blank" rel="noreferrer" data-testid="ebook-download"
                  className="inline-flex items-center gap-2 bg-[#C6899E] text-[#FBF5F1] rounded-full px-6 py-3 text-sm uppercase tracking-[0.15em] hover:bg-[#B4718C] transition-colors">
                  <Download className="w-4 h-4" /> Scarica l'eBook
                </a>
              )}
            </div>
          </div>
        </section>

        {/* Path intro */}
        {paras(data.path_intro?.text).length > 0 && (
          <div className="mb-10">
            <div className="eyebrow mb-3">Il percorso di 21 giorni</div>
            <div className="prose-unfiltered">{paras(data.path_intro.text).map((p, i) => <p key={i}>{p}</p>)}</div>
          </div>
        )}

        {/* Quaderno */}
        {(data.quaderno?.text || data.quaderno?.pdf_path) && (
          <section className="rounded-3xl bg-[#ECD8DE] p-6 sm:p-8 mb-14" data-testid="percorso-quaderno">
            <div className="eyebrow mb-3" style={{ color: "#A76B86" }}>Il Quaderno dei 21 giorni</div>
            {data.quaderno?.text && <p className="text-[#4A2233] leading-relaxed mb-5">{data.quaderno.text}</p>}
            {data.quaderno?.pdf_path && (
              <a href={mediaUrl(data.quaderno.pdf_path)} download target="_blank" rel="noreferrer" data-testid="quaderno-download"
                className="inline-flex items-center gap-2 bg-[#4A2233] text-[#FBF5F1] rounded-full px-6 py-3 text-sm uppercase tracking-[0.15em] hover:opacity-90 transition-opacity">
                <Download className="w-4 h-4" /> Scarica il Quaderno
              </a>
            )}
          </section>
        )}
      </div>

      {/* 21 rooms */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-8 lg:px-16 pb-24">
        <div className="eyebrow mb-6">Le 21 stanze</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
          {data.days.map((d, i) => {
            const locked = !d.available;
            const CardInner = (
              <div className="relative aspect-[3/4] rounded-2xl overflow-hidden bg-[#F3E8E2] border border-[rgba(61,50,56,0.1)]">
                {d.cover_path ? <img src={mediaUrl(d.cover_path)} alt={`Giorno ${d.day}`} className={`w-full h-full object-cover ${locked ? "opacity-40 grayscale" : "group-hover:scale-105 transition-transform duration-700"}`} /> : null}
                <div className="absolute inset-0 bg-gradient-to-t from-[rgba(74,34,51,0.55)] via-transparent to-transparent" />
                <div className="absolute top-3 left-3 text-[#FBF5F1]">
                  <div className="text-[0.6rem] uppercase tracking-[0.18em] opacity-90">Giorno</div>
                  <div className="font-serif text-3xl leading-none">{String(d.day).padStart(2, "0")}</div>
                </div>
                {locked && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-11 h-11 rounded-full bg-[rgba(251,245,241,0.9)] flex items-center justify-center">
                      <Lock className="w-5 h-5 text-[#C6899E]" />
                    </div>
                  </div>
                )}
                <div className="absolute bottom-3 left-3 right-3 text-[#FBF5F1]">
                  {d.title && <div className="font-serif text-lg leading-tight drop-shadow">{d.title}</div>}
                  {!locked && <div className="mt-1 inline-flex items-center gap-1 text-[0.65rem] uppercase tracking-[0.15em] opacity-90">Apri <ArrowUpRight className="w-3 h-3" /></div>}
                </div>
              </div>
            );
            return (
              <motion.div key={d.day} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.4, delay: (i % 8) * 0.04 }}>
                {locked ? (
                  <div className="cursor-not-allowed" data-testid={`day-card-${d.day}`}>{CardInner}</div>
                ) : (
                  <button onClick={() => navigate(`/percorso/giorno/${d.day}`)} className="group block w-full text-left" data-testid={`day-card-${d.day}`}>{CardInner}</button>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
